import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useFirestore } from '../contexts/FirestoreContext';

/**
 * Custom hook for detailed staff data filtered by status
 * Returns individual staff records for specific status types
 */
export const useDetailedStaffData = (orgId, statusFilter = null) => {
  const { db } = useFirestore();
  const [data, setData] = useState({
    staff: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('🧍 Setting up detailed staff data listener for org:', orgId, 'status:', statusFilter);

    const staffRef = collection(db, 'c3_staff_status');
    let q = query(
      staffRef,
      where('orgId', '==', orgId)
      // TODO: Add back orderBy('lastUpdated', 'desc') after creating Firebase index
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        let staffData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Manual sort by lastUpdated since we removed orderBy from query
        staffData.sort((a, b) => {
          const aTime = a.lastUpdated?.toDate?.() || new Date(0);
          const bTime = b.lastUpdated?.toDate?.() || new Date(0);
          return bTime - aTime; // desc order
        });

        // Filter by status if specified
        if (statusFilter) {
          staffData = staffData.filter(staff => staff.status === statusFilter);
        }

        console.log('🧍 Detailed staff data updated:', staffData.length, 'records');
        setData({
          staff: staffData,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Detailed staff data error:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => unsubscribe();
  }, [orgId, db, statusFilter]);

  return data;
};

/**
 * Custom hook for detailed site data filtered by status
 * Returns individual site records for specific status types
 */
export const useDetailedSiteData = (orgId, statusFilter = null) => {
  const { db } = useFirestore();
  const [data, setData] = useState({
    sites: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('🏢 Setting up detailed site data listener for org:', orgId, 'status:', statusFilter);

    const sitesRef = collection(db, 'c3_sites');
    let q = query(
      sitesRef,
      where('organization_id', '==', orgId),
      orderBy('site_name', 'asc')
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        let sitesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter by status if specified
        if (statusFilter) {
          sitesData = sitesData.filter(site => site.status === statusFilter);
        }

        console.log('🏢 Detailed site data updated:', sitesData.length, 'records');
        setData({
          sites: sitesData,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Detailed site data error:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => unsubscribe();
  }, [orgId, db, statusFilter]);

  return data;
};

/**
 * Custom hook for detailed incident data filtered by type
 * Returns individual incident records for specific crisis types
 */
export const useDetailedIncidentData = (orgId, typeFilter = null) => {
  const { db } = useFirestore();
  const [data, setData] = useState({
    incidents: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    if (!orgId || !db) {
      setData(prev => ({ ...prev, loading: false }));
      return;
    }

    console.log('📊 Setting up detailed incident data listener for org:', orgId, 'type:', typeFilter);

    const incidentsRef = collection(db, 'c3_incidents');
    let q = query(
      incidentsRef,
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        let incidentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter out resolved incidents
        incidentsData = incidentsData.filter(incident => 
          incident.status !== 'resolved'
        );

        // Filter by type if specified
        if (typeFilter) {
          incidentsData = incidentsData.filter(incident => 
            incident.crisisType === typeFilter
          );
        }

        console.log('📊 Detailed incident data updated:', incidentsData.length, 'records');
        setData({
          incidents: incidentsData,
          loading: false,
          error: null
        });
      },
      (error) => {
        console.error('❌ Detailed incident data error:', error);
        setData(prev => ({
          ...prev,
          loading: false,
          error: error.message
        }));
      }
    );

    return () => unsubscribe();
  }, [orgId, db, typeFilter]);

  return data;
}; 